import type { PngColorType, PngColorTypeName, PngStructure } from './types.js';

/** The eight-byte signature required at the start of every PNG datastream. */
export const PNG_SIGNATURE = new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);

/** PNG limits dimensions to the positive signed 31-bit integer range. */
export const PNG_MAX_DIMENSION = 0x7fffffff;

/**
 * A conservative default guard against decompression bombs. The parser only
 * reads metadata, but downstream canvas decoding would allocate at least four
 * bytes per pixel.
 */
export const DEFAULT_MAX_PNG_PIXELS = 32_000_000;

export const DEFAULT_MAX_PNG_CHUNKS = 100_000;

export type PngParseErrorCode =
  | 'INVALID_INPUT'
  | 'EMPTY_FILE'
  | 'TRUNCATED_SIGNATURE'
  | 'INVALID_SIGNATURE'
  | 'TRUNCATED_CHUNK'
  | 'INVALID_CHUNK_TYPE'
  | 'CRC_MISMATCH'
  | 'INVALID_CHUNK_ORDER'
  | 'INVALID_IHDR'
  | 'INVALID_DIMENSIONS'
  | 'EXTREME_DIMENSIONS'
  | 'INVALID_COLOR_FORMAT'
  | 'UNSUPPORTED_PNG_METHOD'
  | 'INVALID_TRNS'
  | 'INVALID_PLTE'
  | 'UNKNOWN_CRITICAL_CHUNK'
  | 'MISSING_REQUIRED_CHUNK'
  | 'TRAILING_DATA'
  | 'TOO_MANY_CHUNKS';

export class PngParseError extends Error {
  public readonly code: PngParseErrorCode;

  public readonly offset: number | undefined;

  public constructor(code: PngParseErrorCode, message: string, offset?: number) {
    super(message);
    this.name = 'PngParseError';
    this.code = code;
    this.offset = offset;
  }
}

export interface ParsePngOptions {
  /** Maximum accepted width or height. Cannot exceed the PNG specification. */
  readonly maxDimension?: number;
  /** Maximum accepted width multiplied by height. */
  readonly maxPixels?: number;
  /** Defensive limit for the number of chunks in a datastream. */
  readonly maxChunks?: number;
}

const COLOR_TYPE_NAMES: Readonly<Record<PngColorType, PngColorTypeName>> = {
  0: 'grayscale',
  2: 'truecolor',
  3: 'indexed-color',
  4: 'grayscale-alpha',
  6: 'truecolor-alpha',
};

const VALID_BIT_DEPTHS: Readonly<Record<PngColorType, readonly number[]>> = {
  0: [1, 2, 4, 8, 16],
  2: [8, 16],
  3: [1, 2, 4, 8],
  4: [8, 16],
  6: [8, 16],
};

const KNOWN_CRITICAL_CHUNKS = new Set(['IHDR', 'PLTE', 'IDAT', 'IEND']);

interface IhdrFields {
  readonly width: number;
  readonly height: number;
  readonly bitDepth: number;
  readonly colorType: PngColorType;
  readonly compressionMethod: number;
  readonly filterMethod: number;
  readonly interlaceMethod: number;
}

function fail(code: PngParseErrorCode, message: string, offset?: number): never {
  throw new PngParseError(code, message, offset);
}

function positiveIntegerOption(
  value: number | undefined,
  fallback: number,
  name: string,
  maximum = Number.MAX_SAFE_INTEGER,
): number {
  const resolved = value ?? fallback;
  if (!Number.isSafeInteger(resolved) || resolved < 1 || resolved > maximum) {
    fail('INVALID_INPUT', `${name} must be a positive safe integer no greater than ${maximum}.`);
  }
  return resolved;
}

function isAsciiLetter(value: number): boolean {
  return (value >= 0x41 && value <= 0x5a) || (value >= 0x61 && value <= 0x7a);
}

function readChunkType(view: DataView, offset: number): string {
  const codes = [
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  ];

  if (codes.some((code) => !isAsciiLetter(code))) {
    fail(
      'INVALID_CHUNK_TYPE',
      `PNG chunk at byte ${offset - 4} has a non-alphabetic type.`,
      offset,
    );
  }

  // The reserved bit (bit 5 of the third type byte) must be zero.
  if ((codes[2] & 0x20) !== 0) {
    fail(
      'INVALID_CHUNK_TYPE',
      `PNG chunk at byte ${offset - 4} has an invalid reserved type bit.`,
      offset,
    );
  }

  return String.fromCharCode(codes[0], codes[1], codes[2], codes[3]);
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < table.length; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = (value & 1) === 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[index] = value >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array, start: number, end: number): number {
  let crc = 0xffffffff;
  for (let index = start; index < end; index += 1) {
    const tableIndex = (crc ^ bytes[index]) & 0xff;
    crc = (CRC_TABLE[tableIndex] ?? 0) ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function isPngColorType(value: number): value is PngColorType {
  return value === 0 || value === 2 || value === 3 || value === 4 || value === 6;
}

function parseIhdr(
  view: DataView,
  dataOffset: number,
  length: number,
  maxDimension: number,
  maxPixels: number,
): IhdrFields {
  if (length !== 13) {
    fail(
      'INVALID_IHDR',
      `IHDR must contain exactly 13 data bytes; received ${length}.`,
      dataOffset - 8,
    );
  }

  const width = view.getUint32(dataOffset);
  const height = view.getUint32(dataOffset + 4);
  const bitDepth = view.getUint8(dataOffset + 8);
  const rawColorType = view.getUint8(dataOffset + 9);
  const compressionMethod = view.getUint8(dataOffset + 10);
  const filterMethod = view.getUint8(dataOffset + 11);
  const interlaceMethod = view.getUint8(dataOffset + 12);

  if (width === 0 || height === 0) {
    fail(
      'INVALID_DIMENSIONS',
      `PNG dimensions must be non-zero; received ${width}x${height}.`,
      dataOffset,
    );
  }

  if (
    width > PNG_MAX_DIMENSION ||
    height > PNG_MAX_DIMENSION ||
    width > maxDimension ||
    height > maxDimension
  ) {
    fail(
      'EXTREME_DIMENSIONS',
      `PNG dimensions ${width}x${height} exceed the configured safe limit.`,
      dataOffset,
    );
  }

  // Division avoids overflowing or losing integer precision in width * height.
  if (height > Math.floor(maxPixels / width)) {
    fail(
      'EXTREME_DIMENSIONS',
      `PNG dimensions ${width}x${height} exceed the ${maxPixels.toLocaleString('en-US')}-pixel safety limit.`,
      dataOffset,
    );
  }

  if (!isPngColorType(rawColorType)) {
    fail('INVALID_COLOR_FORMAT', `Unsupported PNG color type ${rawColorType}.`, dataOffset + 9);
  }

  if (!VALID_BIT_DEPTHS[rawColorType].includes(bitDepth)) {
    fail(
      'INVALID_COLOR_FORMAT',
      `Bit depth ${bitDepth} is invalid for PNG color type ${rawColorType}.`,
      dataOffset + 8,
    );
  }

  if (compressionMethod !== 0) {
    fail(
      'UNSUPPORTED_PNG_METHOD',
      `Unsupported PNG compression method ${compressionMethod}.`,
      dataOffset + 10,
    );
  }
  if (filterMethod !== 0) {
    fail(
      'UNSUPPORTED_PNG_METHOD',
      `Unsupported PNG filter method ${filterMethod}.`,
      dataOffset + 11,
    );
  }
  if (interlaceMethod !== 0 && interlaceMethod !== 1) {
    fail(
      'UNSUPPORTED_PNG_METHOD',
      `Unsupported PNG interlace method ${interlaceMethod}.`,
      dataOffset + 12,
    );
  }

  return {
    width,
    height,
    bitDepth,
    colorType: rawColorType,
    compressionMethod,
    filterMethod,
    interlaceMethod,
  };
}

function validateTrns(
  view: DataView,
  dataOffset: number,
  length: number,
  ihdr: IhdrFields,
  paletteEntries: number,
): void {
  if (ihdr.colorType === 4 || ihdr.colorType === 6) {
    fail(
      'INVALID_TRNS',
      `tRNS is forbidden for color type ${ihdr.colorType}, which already contains alpha.`,
      dataOffset - 8,
    );
  }

  if (ihdr.colorType === 0) {
    if (length !== 2) {
      fail('INVALID_TRNS', 'Grayscale tRNS data must be 2 bytes.', dataOffset - 8);
    }
    const transparentSample = view.getUint16(dataOffset);
    const maximumSample = 2 ** ihdr.bitDepth - 1;
    if (transparentSample > maximumSample) {
      fail('INVALID_TRNS', 'Grayscale tRNS sample exceeds the image bit depth.', dataOffset);
    }
    return;
  }

  if (ihdr.colorType === 2) {
    if (length !== 6) {
      fail('INVALID_TRNS', 'Truecolor tRNS data must be 6 bytes.', dataOffset - 8);
    }
    if (ihdr.bitDepth === 8) {
      for (let channel = 0; channel < 3; channel += 1) {
        if (view.getUint16(dataOffset + channel * 2) > 0xff) {
          fail(
            'INVALID_TRNS',
            'Truecolor tRNS sample exceeds the image bit depth.',
            dataOffset + channel * 2,
          );
        }
      }
    }
    return;
  }

  if (paletteEntries === 0) {
    fail('INVALID_CHUNK_ORDER', 'Indexed-color tRNS must follow a PLTE chunk.', dataOffset - 8);
  }
  if (length < 1 || length > paletteEntries) {
    fail(
      'INVALID_TRNS',
      `Indexed-color tRNS length must be between 1 and ${paletteEntries}; received ${length}.`,
      dataOffset - 8,
    );
  }
}

/** Returns whether the PNG color type stores an alpha sample per pixel. */
export function pngColorTypeHasAlphaChannel(colorType: PngColorType): boolean {
  return colorType === 4 || colorType === 6;
}

/**
 * Returns whether this structure can encode transparent pixels. For color
 * types without an alpha sample, transparency requires a valid tRNS chunk.
 */
export function pngColorTypeSupportsTransparency(
  colorType: PngColorType,
  hasTrns = false,
): boolean {
  return pngColorTypeHasAlphaChannel(colorType) || hasTrns;
}

export function pngColorTypeName(colorType: PngColorType): PngColorTypeName {
  return COLOR_TYPE_NAMES[colorType];
}

/**
 * Parses and validates a complete PNG datastream without decoding image data.
 * The returned object contains metadata only and never retains the input bytes.
 */
export function parsePng(buffer: ArrayBuffer, options: ParsePngOptions = {}): PngStructure {
  let view: DataView;
  try {
    view = new DataView(buffer);
  } catch {
    fail('INVALID_INPUT', 'Expected a readable ArrayBuffer.');
  }

  const byteLength = view.byteLength;
  if (byteLength === 0) {
    fail('EMPTY_FILE', 'PNG file is empty.', 0);
  }
  if (byteLength < PNG_SIGNATURE.length) {
    fail(
      'TRUNCATED_SIGNATURE',
      `PNG signature is truncated: expected 8 bytes, received ${byteLength}.`,
      0,
    );
  }

  for (let index = 0; index < PNG_SIGNATURE.length; index += 1) {
    if (view.getUint8(index) !== PNG_SIGNATURE[index]) {
      fail('INVALID_SIGNATURE', 'Invalid PNG signature.', index);
    }
  }

  const maxDimension = positiveIntegerOption(
    options.maxDimension,
    PNG_MAX_DIMENSION,
    'maxDimension',
    PNG_MAX_DIMENSION,
  );
  const maxPixels = positiveIntegerOption(options.maxPixels, DEFAULT_MAX_PNG_PIXELS, 'maxPixels');
  const maxChunks = positiveIntegerOption(options.maxChunks, DEFAULT_MAX_PNG_CHUNKS, 'maxChunks');

  const bytes = new Uint8Array(buffer);
  const chunks: string[] = [];
  let offset = PNG_SIGNATURE.length;
  let ihdr: IhdrFields | undefined;
  let sawPlte = false;
  let paletteEntries = 0;
  let hasTrns = false;
  let sawIdat = false;
  let idatSequenceEnded = false;
  let sawIend = false;

  while (offset < byteLength) {
    if (chunks.length >= maxChunks) {
      fail(
        'TOO_MANY_CHUNKS',
        `PNG contains more than ${maxChunks.toLocaleString('en-US')} chunks.`,
        offset,
      );
    }
    if (byteLength - offset < 12) {
      fail('TRUNCATED_CHUNK', `PNG chunk beginning at byte ${offset} is truncated.`, offset);
    }

    const chunkOffset = offset;
    const length = view.getUint32(chunkOffset);
    const typeOffset = chunkOffset + 4;
    const type = readChunkType(view, typeOffset);
    const dataOffset = chunkOffset + 8;
    const availableDataBytes = byteLength - dataOffset - 4;

    if (length > availableDataBytes) {
      fail(
        'TRUNCATED_CHUNK',
        `${type} declares ${length} data bytes, but the PNG ends early.`,
        chunkOffset,
      );
    }

    const crcOffset = dataOffset + length;
    const expectedCrc = view.getUint32(crcOffset);
    const actualCrc = crc32(bytes, typeOffset, crcOffset);
    if (actualCrc !== expectedCrc) {
      fail(
        'CRC_MISMATCH',
        `${type} has an invalid CRC (expected 0x${expectedCrc.toString(16).padStart(8, '0')}, calculated 0x${actualCrc.toString(16).padStart(8, '0')}).`,
        crcOffset,
      );
    }

    offset = crcOffset + 4;
    chunks.push(type);

    if (chunks.length === 1 && type !== 'IHDR') {
      fail('INVALID_CHUNK_ORDER', `IHDR must be the first PNG chunk; found ${type}.`, chunkOffset);
    }

    if (type !== 'IHDR' && ihdr === undefined) {
      fail(
        'MISSING_REQUIRED_CHUNK',
        `${type} appeared before the required IHDR chunk.`,
        chunkOffset,
      );
    }

    if (type === 'IDAT') {
      if (idatSequenceEnded) {
        fail('INVALID_CHUNK_ORDER', 'IDAT chunks must be consecutive.', chunkOffset);
      }
    } else if (sawIdat && type !== 'IEND') {
      idatSequenceEnded = true;
    }

    switch (type) {
      case 'IHDR': {
        if (ihdr !== undefined || chunks.length !== 1) {
          fail('INVALID_CHUNK_ORDER', 'PNG contains more than one IHDR chunk.', chunkOffset);
        }
        ihdr = parseIhdr(view, dataOffset, length, maxDimension, maxPixels);
        break;
      }

      case 'PLTE': {
        if (sawPlte) {
          fail('INVALID_PLTE', 'PNG contains more than one PLTE chunk.', chunkOffset);
        }
        if (sawIdat || hasTrns) {
          fail('INVALID_CHUNK_ORDER', 'PLTE must appear before tRNS and IDAT.', chunkOffset);
        }
        if (ihdr?.colorType === 0 || ihdr?.colorType === 4) {
          fail('INVALID_PLTE', `PLTE is forbidden for color type ${ihdr.colorType}.`, chunkOffset);
        }
        if (length === 0 || length > 768 || length % 3 !== 0) {
          fail(
            'INVALID_PLTE',
            'PLTE length must contain 1 to 256 three-byte palette entries.',
            chunkOffset,
          );
        }
        paletteEntries = length / 3;
        if (ihdr?.colorType === 3 && paletteEntries > 2 ** ihdr.bitDepth) {
          fail(
            'INVALID_PLTE',
            'PLTE contains more entries than the indexed bit depth permits.',
            chunkOffset,
          );
        }
        sawPlte = true;
        break;
      }

      case 'tRNS': {
        if (hasTrns) {
          fail('INVALID_TRNS', 'PNG contains more than one tRNS chunk.', chunkOffset);
        }
        if (sawIdat) {
          fail('INVALID_CHUNK_ORDER', 'tRNS must appear before IDAT.', chunkOffset);
        }
        if (ihdr === undefined) {
          fail('MISSING_REQUIRED_CHUNK', 'tRNS requires IHDR.', chunkOffset);
        }
        validateTrns(view, dataOffset, length, ihdr, paletteEntries);
        hasTrns = true;
        break;
      }

      case 'IDAT': {
        if (ihdr?.colorType === 3 && !sawPlte) {
          fail(
            'MISSING_REQUIRED_CHUNK',
            'Indexed-color PNG requires PLTE before IDAT.',
            chunkOffset,
          );
        }
        sawIdat = true;
        break;
      }

      case 'IEND': {
        if (length !== 0) {
          fail('INVALID_CHUNK_ORDER', 'IEND must have a zero data length.', chunkOffset);
        }
        if (!sawIdat) {
          fail(
            'MISSING_REQUIRED_CHUNK',
            'PNG must contain at least one IDAT chunk before IEND.',
            chunkOffset,
          );
        }
        sawIend = true;
        if (offset !== byteLength) {
          fail('TRAILING_DATA', 'PNG contains data after IEND.', offset);
        }
        break;
      }

      default: {
        const isCritical = (type.charCodeAt(0) & 0x20) === 0;
        if (isCritical && !KNOWN_CRITICAL_CHUNKS.has(type)) {
          fail('UNKNOWN_CRITICAL_CHUNK', `Unsupported critical PNG chunk ${type}.`, chunkOffset);
        }
      }
    }

    if (sawIend) {
      break;
    }
  }

  if (ihdr === undefined) {
    fail('MISSING_REQUIRED_CHUNK', 'PNG is missing its IHDR chunk.', offset);
  }
  if (!sawIdat) {
    fail('MISSING_REQUIRED_CHUNK', 'PNG is missing an IDAT chunk.', offset);
  }
  if (!sawIend) {
    fail('MISSING_REQUIRED_CHUNK', 'PNG is missing its IEND chunk.', offset);
  }
  if (ihdr.colorType === 3 && !sawPlte) {
    fail('MISSING_REQUIRED_CHUNK', 'Indexed-color PNG is missing PLTE.', offset);
  }

  const hasAlphaChannel = pngColorTypeHasAlphaChannel(ihdr.colorType);

  return {
    valid: true,
    signatureValid: true,
    width: ihdr.width,
    height: ihdr.height,
    bitDepth: ihdr.bitDepth,
    colorType: ihdr.colorType,
    colorTypeName: pngColorTypeName(ihdr.colorType),
    compressionMethod: ihdr.compressionMethod,
    filterMethod: ihdr.filterMethod,
    interlaceMethod: ihdr.interlaceMethod,
    hasTrns,
    hasTRNS: hasTrns,
    hasAlphaChannel,
    supportsTransparency: hasAlphaChannel || hasTrns,
    chunks,
  };
}

/** Compatibility alias for consumers that prefer the more explicit name. */
export const parsePngStructure = parsePng;
